import { PageTypeEnum } from '@/constant';
import {
	GetWorkerChangeWorkPlaceListResponse,
	IApplicantListItem,
	IFranchiseListItem,
	IFranchiseNewListItem,
	IWorkerChangeWorkPlaceDetail,
} from '@/services';

import {
	convertAllocationRestrictedToName,
	convertApplicantTypeCodeToName,
	convertCaroEnumToName,
	convertExpirationDate,
	convertFranchiseTypeCodeToName,
	convertFranchiseTypeDetailCodeToName,
	convertGenderCodeToName,
	convertGradeCodeToName,
	convertNationCodeToName,
	convertProductionTypeCodeToName,
	convertProductionUnitCodeToName,
	convertReasonCodeToName,
	convertReasonDettailCodeToName,
	convertVisaCodeToName,
	filterByPrefixKeyWithFormat,
	formatDate,
	insertObjectIfV2,
	map,
} from 'utils';

import { isNotEmpty } from 'ramda';
import { isNotNilOrEmpty } from 'ramda-adjunct';

import { formatWorkerApplicantListItem } from '../worker-applicant';
import {
	formatWorkerFranchiseListItem,
	formatWorkerFranchiseNewListItem,
} from './../worker-franchise/worker-franchise.helper';

export const formatWorkerChangeWorkPlaceListDetail = (
	input: GetWorkerChangeWorkPlaceListResponse['content'][number],
	formatExcel: boolean = false
) => {
	let _input = input;

	let franchiseFormat = filterByPrefixKeyWithFormat<IFranchiseListItem>(
		_input,
		PageTypeEnum.FRANCHISE
	);
	if (isNotEmpty(franchiseFormat)) {
		franchiseFormat = {
			...formatWorkerFranchiseListItem(franchiseFormat),
			franchiseTypeDetail: convertFranchiseTypeDetailCodeToName(
				input.franchiseTypeDetail
			),
			franchiseProductionUnit: convertProductionUnitCodeToName(
				input.franchiseProductionUnit
			),
			franchiseProductionType: convertProductionTypeCodeToName(
				input.franchiseProductionType
			),
			...insertObjectIfV2(formatExcel, {
				franchiseType: convertFranchiseTypeCodeToName(input.franchiseType),
				franchiseDistrictId: input.franchiseDistrictName,
				franchisePublicFranchiseId: input.franchisePublicFranchiseName,
				franchiseProductionScale: isNotNilOrEmpty(
					input?.franchiseProductionScale
				)
					? Number(input?.franchiseProductionScale)?.toLocaleString?.('ko-KR')
					: '',
				franchiseHasLodging: convertCaroEnumToName(input.franchiseHasLodging),

				franchiseIsAllocationRestricted: convertAllocationRestrictedToName(
					input.franchiseIsAllocationRestricted
				),
			}),
		};
	}

	let applicantFormat = filterByPrefixKeyWithFormat<IApplicantListItem>(
		_input,
		PageTypeEnum.APPLICANT
	);
	if (isNotEmpty(applicantFormat)) {
		applicantFormat = {
			...formatWorkerApplicantListItem(applicantFormat),
			...insertObjectIfV2(formatExcel, {
				applicantGender: convertGenderCodeToName(input.applicantGender),
				applicantDistrictId: input.applicantDistrictName,
				applicantNationalCode: convertNationCodeToName(
					input.applicantNationalCode
				),
				applicantPublicFranchiseId: input.applicantPublicFranchiseName,
				applicantStatusId: input.applicantStatusName,
				applicantIssuanceStatusId: input.applicantIssuanceStatusName,
				applicantApplicationType: convertApplicantTypeCodeToName(
					input.applicantApplicationType
				),
				applicantIsReEntry: convertCaroEnumToName(input.applicantIsReEntry),
				applicantIsPublic: convertCaroEnumToName(input.applicantIsPublic),
				applicantGrade: convertGradeCodeToName(input.applicantGrade),
				applicantDepartureDate: convertExpirationDate(
					input.applicantDepartureDate
				),
				applicantExpirationDate: convertExpirationDate(
					input.applicantExpirationDate
				),
			}),
		};
	}

	let newFranchiseFormat = filterByPrefixKeyWithFormat<IFranchiseNewListItem>(
		_input,
		PageTypeEnum.FRANCHISE_NEW
	);
	if (isNotEmpty(newFranchiseFormat)) {
		newFranchiseFormat = {
			...formatWorkerFranchiseNewListItem(newFranchiseFormat),
			franchiseNewPublicFranchiseId:
				input.franchiseNewPublicFranchiseName?.toString() || '',
			franchiseNewTypeDetail: convertFranchiseTypeDetailCodeToName(
				input.franchiseNewTypeDetail
			),
			franchiseNewProductionUnit: convertProductionUnitCodeToName(
				input.franchiseNewProductionUnit
			),
			franchiseNewProductionType: convertProductionTypeCodeToName(
				input.franchiseNewProductionType
			),
			...insertObjectIfV2(formatExcel, {
				franchiseNewType: convertFranchiseTypeCodeToName(
					input.franchiseNewType
				),
				franchiseNewDistrictId: input.franchiseNewDistrictName,

				franchiseNewProductionScale: isNotNilOrEmpty(
					input?.franchiseNewProductionScale
				)
					? Number(input?.franchiseNewProductionScale)?.toLocaleString?.(
							'ko-KR'
						)
					: '',
				franchiseNewHasLodging: convertCaroEnumToName(
					input.franchiseNewHasLodging
				),

				franchiseNewIsAllocationRestricted: convertAllocationRestrictedToName(
					input.franchiseNewIsAllocationRestricted
				),
			}),
		};
	}

	const formatValues = {
		...franchiseFormat,
		...applicantFormat,
		...newFranchiseFormat,
	} as typeof _input;

	return {
		..._input,
		...insertObjectIfV2(isNotEmpty(formatValues), {
			...formatValues,
		}),
		id: _input.changeWorkPlaceId,
		changeWorkPlaceAssignmentDate: formatDate(
			_input.changeWorkPlaceAssignmentDate
		),
		changeWorkPlaceRegistDt: formatDate(_input.changeWorkPlaceRegistDt),
		changeWorkPlaceUpdateDt: formatDate(_input.changeWorkPlaceUpdateDt),
		...insertObjectIfV2(formatExcel, {
			changeWorkPlaceReason: convertReasonCodeToName(
				_input.changeWorkPlaceReason
			),
			changeWorkPlaceDetailReason: convertReasonDettailCodeToName(
				_input.changeWorkPlaceDetailReason
			),
		}),
	};
};

export const formatWorkerChangeWorkPlaceList = (
	input: GetWorkerChangeWorkPlaceListResponse['content']
) => map(input, (_) => formatWorkerChangeWorkPlaceListDetail(_));

export const formatWorkerChangeWorkPlaceExcelList = (
	input: GetWorkerChangeWorkPlaceListResponse['content']
) => {
	return map(input, (item) => {
		return {
			...formatWorkerChangeWorkPlaceListDetail(item, true),
		};
	});
};

export const formatWorkerChangeWorkPlaceDetail = (
	input: IWorkerChangeWorkPlaceDetail
) => {
	return {
		...input,
		prevWorkStartDate: formatDate(input.prevWorkStartDate),
		prevWorkEndDate: formatDate(input.prevWorkEndDate),
		prevRegistDt: formatDate(input.prevRegistDt),
		assignmentDate: formatDate(input.assignmentDate),
		newStartDate: formatDate(input.newStartDate),
		newEndDate: formatDate(input.newEndDate),
		newWorkStartDate: formatDate(input.newWorkStartDate),
		newWorkEndDate: formatDate(input.newWorkEndDate),
		applicationBirthDate: formatDate(input.applicationBirthDate),
		applicationVisaCodeName: convertVisaCodeToName(input.applicationVisaCode),
		registDt: formatDate(input.registDt),
		updateDt: formatDate(input.updateDt),
	};
};
